import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Employee } from '../../../types';
import { apiClient } from '../../../api/apiClient';
import { fetchAllData } from '../../../store/slices/dataSlice';

interface EmployeeState {
  employees: Employee[];
  loading: boolean;
  error: string | null;
}

const initialState: EmployeeState = {
  employees: [],
  loading: false,
  error: null,
};

export const createEmployeeAsync = createAsyncThunk(
  'employees/create',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/employees', payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create employee');
    }
  }
);

export const removeEmployeeAsync = createAsyncThunk(
  'employees/remove',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/employees/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete employee');
    }
  }
);

export const updateEmployeeAsync = createAsyncThunk(
  'employees/update',
  async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/employees/${id}`, payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update employee');
    }
  }
);

const employeeSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Hydrate from massive data fetch
      .addCase(fetchAllData.fulfilled, (state, action) => {
        state.employees = action.payload.employees || [];
      })
      // Create
      .addCase(createEmployeeAsync.fulfilled, (state, action) => {
        state.employees.push(action.payload);
      })
      // Update
      .addCase(updateEmployeeAsync.fulfilled, (state, action) => {
        const updatedEmp = action.payload;
        const idx = state.employees.findIndex((e: any) => e.id === updatedEmp.id || e._id === updatedEmp._id);
        if (idx !== -1) {
          state.employees[idx] = updatedEmp;
        }
      })
      // Remove
      .addCase(removeEmployeeAsync.fulfilled, (state, action) => {
        state.employees = state.employees.filter((e: any) => e.id !== action.payload && e._id !== action.payload);
      });
  },
});

export default employeeSlice.reducer;
